import { QRType } from "@/types/qr";

const PREVIEW_KEY_MAP: Record<string, Record<string, string>> = {
  mp3: {
    mp3Title: "preview_title",
    mp3ButtonText: "preview_button_text_label",
  },
  vcard: {
    profession: "preview_profession_initial",
    email: "preview_email_initial",
    website: "preview_website_initial",
  },
  business: {
    title: "preview_title_initial",
    companyName: "preview_company_name_initial",
    subtitle: "preview_subtitle_initial",
    buttonText: "preview_button_text_label_initial",
  },
  social: {
    headline: "preview_headline",
    description: "preview_description",
  },
  apps: {
    appsName: "preview_apps_name",
    appsCompany: "preview_apps_company",
    appsDescription: "preview_apps_description",
  },
  video: {
    companyName: "preview_company_name",
    title: "preview_title",
    description: "preview_description",
    buttonText: "preview_button_text",
  },
  pdf: {
    companyName: "preview_company_name",
    pdfName: "preview_pdf_name",
    pdfDescription: "preview_description",
    pdfFileName: "preview_pdf_file_name",
  },
  listoflinks: {
    title: "preview_title",
    description: "preview_description",
  },
  image: {
    albumDescription: "preview_album_description",
    albumTitle: "preview_album_title",
    buttonText: "preview_button_text",
  },
  website: {
    companyName: "preview_company_name",
    title: "preview_title",
    subtitle: "preview_subtitle",
    buttonText: "preview_button_text",
  },
  event: {
    infoTitle: "preview_event_name",
    infoDescription: "preview_description",
    infoActionTitle: "preview_button_text",
  },
  feedback: {
    title: "preview_title",
    subtitle: "preview_subtitle",
  },
  coupon: {
    infoCompanyName: "preview_company_name",
    infoHeadline: "preview_headline",
    infoDescription: "preview_description",
    infoSalesBadge: "preview_sales_badge",
    infoButtonText: "preview_button_text",
  },
  sms: {
    message: "preview_message",
  },
  whatsapp: {
    message: "preview_message",
  },
  email: {
    subject: "preview_subject",
    message: "preview_message",
  },
};

const handleListoflinksMerge = (mergedData: any, translatedData: any) => {
  if (!mergedData.links || !Array.isArray(mergedData.links)) {
    return;
  }

  mergedData.links = mergedData.links.map((link: any, index: number) => {
    const translatedText = translatedData[`preview_text_link_${index}`];
    if (translatedText) link.linkText = translatedText;

    return link;
  });
};

const CUSTOM_MERGE_HANDLERS: Record<string, (mergedData: any, translatedData: any) => void> = {
  listoflinks: handleListoflinksMerge,
};
// ----------------------------------------------------

/**
 * Merges the initPreviewData (JSON) with the translated titles
 * when hovering over the QR selector.
 * * @param initPreviewData - The initial JSON data object for the QR type.
 * @param previewTranslations - Global translations object { mp3: { common: {...} } }.
 * @param qrType - The slug of the QR type (e.g., 'mp3', 'vcard').
 * @returns The initial data merged with the translations.
 */
export const mergePreviewDataWithTranslations = (
  initPreviewData: any,
  previewTranslations: any,
  qrType: QRType
): any => {
  if (!initPreviewData || !previewTranslations || !qrType) {
    return initPreviewData;
  }

  const keyMap = PREVIEW_KEY_MAP[qrType];
  if (!keyMap) {
    return initPreviewData;
  }

  const namespaceData = previewTranslations[qrType];
  const translatedData = namespaceData?.common || namespaceData;

  if (!translatedData) {
    return initPreviewData;
  }

  const mergedData = JSON.parse(JSON.stringify(initPreviewData.data));

  for (const [dataKey, translationKey] of Object.entries(keyMap)) {
    if (translatedData[translationKey]) {
      mergedData[dataKey] = translatedData[translationKey];
    }
  }

  const customHandler = CUSTOM_MERGE_HANDLERS[qrType];
  if (customHandler) {
    customHandler(mergedData, translatedData);
  }

  return {
    ...initPreviewData,
    data: mergedData,
  };
};
